// Gemini AI service for Dark Spot Detection
// Adapted from ./darkspot/gemini.ts for PWA usage

// Note: This service requires GEMINI_API_KEY environment variable
// For PWA, you'll need to configure this in your build process

import logger from "../utils/logger";

class GeminiService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
    this.baseUrl = "https://generativelanguage.googleapis.com/v1beta";

    // Check API key configuration (only log error if missing, not the key status)
    if (!this.apiKey) {
      logger.error(
        "REACT_APP_GEMINI_API_KEY is not set in environment variables"
      );
    } else {
      logger.debug("Gemini API Key configured");
    }
  }

  async analyzeDarkSpots(imageBuffer) {
    try {
      // Check if API key is available
      if (!this.apiKey) {
        throw new Error(
          "Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your .env file"
        );
      }

      // Convert Uint8Array to base64
      let base64Image;
      if (imageBuffer instanceof Uint8Array) {
        // Convert Uint8Array to base64
        const binaryString = Array.from(imageBuffer, (byte) =>
          String.fromCharCode(byte)
        ).join("");
        base64Image = btoa(binaryString);
      } else {
        // Fallback for Buffer (Node.js)
        base64Image = imageBuffer.toString("base64");
      }
      logger.debug("Image buffer length:", imageBuffer.length);
      logger.debug("Base64 image length:", base64Image.length);

      const systemPrompt = `Anda adalah ahli analisis kualitas MMS (multivitamin) yang sangat berpengalaman. 
Tugas Anda adalah menganalisis foto MMS dan mengklasifikasikan berdasarkan dark spots/bintik hitam.

LANGKAH PERTAMA - DETEKSI PILL MMS:
Sebelum menganalisis dark spots, pastikan ada pill MMS dalam foto. Pill MMS memiliki karakteristik:
- Bentuk: Bulat/lingkaran kecil
- Warna: Coklat kecoklatan atau coklat gelap
- Ukuran: Kecil seperti tablet/pil
- Tekstur: Permukaan halus

Jika TIDAK terdeteksi pill MMS dalam foto, berikan respon:
{
  "classification": "no_pills",
  "confidence": 1.0,
  "description": "Tidak terdeteksi adanya Pill MMS dalam foto",
  "spotCount": 0
}

Jika TERDETEKSI pill MMS, lanjutkan analisis dark spots dengan klasifikasi:
- "none": Tidak ada bintik hitam, MMS berkualitas baik (0-2 bintik kecil)
- "medium": Ada bintik sedang, kualitas moderate (3-10 bintik atau beberapa bintik menengah) 
- "many": Ada bintik banyak, kualitas buruk (>10 bintik atau bintik besar yang jelas terlihat)

INSTRUKSI ANALISIS (jika pill MMS terdeteksi):
1. Periksa seluruh permukaan MMS dengan teliti
2. Hitung jumlah dark spots/bintik hitam yang terlihat
3. Perhatikan ukuran dan intensitas bintik
4. Berikan confidence score 0.0-1.0 berdasarkan kejelasan analisis
5. Sertakan deskripsi singkat kondisi MMS dalam bahasa Indonesia

Respon harus dalam format JSON:
{
  "classification": "no_pills|none|medium|many",
  "confidence": 0.85,
  "description": "Deskripsi kondisi MMS atau 'Tidak terdeteksi adanya Pill MMS'",
  "spotCount": jumlah_bintik_yang_terlihat
}`;

      logger.debug("Sending request to Gemini API...");

      const requestBody = {
        contents: [
          {
            parts: [
              {
                text: systemPrompt,
              },
              {
                inlineData: {
                  data: base64Image,
                  mimeType: "image/jpeg",
                },
              },
              {
                text: "Analisis foto MMS ini untuk mendeteksi dark spots dan klasifikasikan sesuai instruksi.",
              },
            ],
          },
        ],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: {
            type: "object",
            properties: {
              classification: {
                type: "string",
                enum: ["no_pills", "none", "medium", "many"],
              },
              confidence: {
                type: "number",
                minimum: 0,
                maximum: 1,
              },
              description: { type: "string" },
              spotCount: { type: "number" },
            },
            required: ["classification", "confidence", "description"],
          },
        },
      };

      const response = await fetch(
        `${this.baseUrl}/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      logger.debug("Response status:", response.status);
      logger.debug("Response ok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        logger.apiError("analyzeDarkSpots", {
          message: errorText,
          status: response.status,
        });
        
        // Try to parse error JSON for better error messages
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error?.message) {
            errorMessage = errorJson.error.message;
            
            // Check for specific error types
            if (errorMessage.includes("API key") || errorMessage.includes("invalid API key")) {
              errorMessage = "Invalid or missing Gemini API key. Please check REACT_APP_GEMINI_API_KEY configuration.";
            } else if (errorMessage.includes("quota") || errorMessage.includes("Quota exceeded")) {
              errorMessage = "Gemini API quota exceeded. Please check your API plan and billing.";
            }
          }
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      logger.apiResponse("analyzeDarkSpots", data);

      // Check for errors in response
      if (data.error) {
        throw new Error(data.error.message || "API returned an error");
      }

      // Check if candidates exist
      if (!data.candidates || data.candidates.length === 0) {
        logger.error("Full API response:", JSON.stringify(data, null, 2));
        throw new Error("No candidates returned from Gemini API. Check logs for details.");
      }

      const rawJson = data.candidates[0]?.content?.parts?.[0]?.text;

      logger.debug(`Gemini Analysis Response: ${rawJson}`);

      if (rawJson) {
        let result;
        try {
          result = JSON.parse(rawJson);
        } catch (parseError) {
          logger.error("Failed to parse JSON response:", rawJson);
          throw new Error(`Failed to parse AI response: ${parseError.message}`);
        }

        // Validasi dan sanitasi hasil
        if (
          !["no_pills", "none", "medium", "many"].includes(
            result.classification
          )
        ) {
          throw new Error(`Invalid classification from Gemini: ${result.classification}`);
        }

        result.confidence = Math.max(0, Math.min(1, result.confidence || 0.5));

        return result;
      } else {
        logger.error("Empty response from Gemini model. Full response:", JSON.stringify(data, null, 2));
        throw new Error("Empty response from Gemini model. Check logs for details.");
      }
    } catch (error) {
      logger.apiError("analyzeDarkSpots", error);
      // Re-throw with original error message for better debugging
      if (error.message) {
        throw error;
      }
      throw new Error(`Failed to analyze dark spots: ${error.message || "Unknown error"}`);
    }
  }

  async summarizeArticle(text) {
    try {
      const prompt = `Please summarize the following text concisely while maintaining key points:\n\n${text}`;

      const response = await fetch(
        `${this.baseUrl}/models/gemini-1.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: prompt,
                  },
                ],
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return (
        data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "Something went wrong"
      );
    } catch (error) {
      throw new Error(`Failed to summarize article: ${error.message}`);
    }
  }

  async analyzeSentiment(text) {
    try {
      const systemPrompt = `You are a sentiment analysis expert. 
Analyze the sentiment of the text and provide a rating
from 1 to 5 stars and a confidence score between 0 and 1.
Respond with JSON in this format: 
{'rating': number, 'confidence': number}`;

      const response = await fetch(
        `${this.baseUrl}/models/gemini-1.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: systemPrompt,
                  },
                  {
                    text: text,
                  },
                ],
              },
            ],
            generationConfig: {
              responseMimeType: "application/json",
              responseSchema: {
                type: "object",
                properties: {
                  rating: { type: "number" },
                  confidence: { type: "number" },
                },
                required: ["rating", "confidence"],
              },
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const rawJson = data.candidates?.[0]?.content?.parts?.[0]?.text;

      logger.debug(`Raw JSON: ${rawJson}`);

      if (rawJson) {
        return JSON.parse(rawJson);
      } else {
        throw new Error("Empty response from model");
      }
    } catch (error) {
      throw new Error(`Failed to analyze sentiment: ${error.message}`);
    }
  }

  async analyzeMMSBlister(imageBuffer) {
    try {
      // Check if API key is available
      if (!this.apiKey) {
        throw new Error(
          "Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your .env file"
        );
      }

      // Convert Uint8Array to base64
      let base64Image;
      if (imageBuffer instanceof Uint8Array) {
        const binaryString = Array.from(imageBuffer, (byte) =>
          String.fromCharCode(byte)
        ).join("");
        base64Image = btoa(binaryString);
      } else {
        base64Image = imageBuffer.toString("base64");
      }

      const systemPrompt = `You are an expert in pharmaceutical packaging analysis. 
Your task is to analyze the provided image and count the number of white calcium tablets in a blister pack.

INSTRUCTIONS:
1. Look for white tablets arranged in a grid pattern within a blister pack
2. Each tablet should be white in color and circular/oval in shape
3. Count only intact tablets that are clearly visible
4. Ignore any damaged, missing, or partially visible tablets
5. Provide a confidence score based on image clarity and tablet visibility

Respond with JSON in this format:
{
  "tabletCount": number,
  "confidence": number (0.0-1.0),
  "description": "Brief description of what you found"
}`;

      const requestBody = {
        contents: [
          {
            parts: [
              {
                text: systemPrompt,
              },
              {
                inlineData: {
                  data: base64Image,
                  mimeType: "image/jpeg",
                },
              },
              {
                text: "Based on the image provided, count the number of tablets in the blister pack. Each tablet is white. The tablets are arranged in a grid. There are a total of 10 tablets in this blister pack. Please determine the number of tablets in the pack.",
              },
            ],
          },
        ],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: {
            type: "object",
            properties: {
              tabletCount: {
                type: "number",
                minimum: 0,
              },
              confidence: {
                type: "number",
                minimum: 0,
                maximum: 1,
              },
              description: { type: "string" },
            },
            required: ["tabletCount", "confidence", "description"],
          },
        },
      };

      const response = await fetch(
        `${this.baseUrl}/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logger.apiError("analyzeMMSBlister", {
          message: errorText,
          status: response.status,
        });
        let errorMessage = `HTTP error! status: ${response.status}`;

        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error?.message) {
            errorMessage = errorJson.error.message;

            // Check for quota exceeded error
            if (
              errorMessage.includes("quota") ||
              errorMessage.includes("Quota exceeded")
            ) {
              throw new Error(
                "Gemini API quota has been exceeded. Please check your API plan and billing details. " +
                  "You may need to upgrade your plan or wait for quota reset. " +
                  "For more information: https://ai.google.dev/gemini-api/docs/rate-limits"
              );
            }
          } else {
            errorMessage = errorText;
          }
        } catch (e) {
          // If it's already our quota error, re-throw it
          if (e.message && e.message.includes("quota")) {
            throw e;
          }
          errorMessage = errorText || `HTTP ${response.status} error`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Check for errors in response
      if (data.error) {
        throw new Error(data.error.message || "API returned an error");
      }

      // Check if candidates exist
      if (!data.candidates || data.candidates.length === 0) {
        throw new Error("No candidates returned from Gemini API");
      }

      const rawJson = data.candidates[0]?.content?.parts?.[0]?.text;

      if (!rawJson) {
        logger.error("Full API response:", JSON.stringify(data, null, 2));
        throw new Error(
          "Empty response from Gemini model. Check logs for details."
        );
      }

      try {
        const result = JSON.parse(rawJson);

        // Validate required fields
        if (typeof result.tabletCount !== "number") {
          throw new Error("Invalid response: tabletCount is not a number");
        }

        result.confidence = Math.max(0, Math.min(1, result.confidence || 0.5));
        return result;
      } catch (parseError) {
        logger.error("Failed to parse JSON response:", rawJson);
        throw new Error(`Failed to parse AI response: ${parseError.message}`);
      }
    } catch (error) {
      logger.apiError("analyzeMMSBlister", error);
      // Re-throw with original error message for better debugging
      if (error.message) {
        throw error;
      }
      throw new Error(
        `Failed to analyze MMS blister tablets: ${error.message || "Unknown error"}`
      );
    }
  }

  async analyzeTTDCapsules(imageBuffer) {
    try {
      // Check if API key is available
      if (!this.apiKey) {
        throw new Error(
          "Gemini API key is not configured. Please set REACT_APP_GEMINI_API_KEY in your .env file"
        );
      }

      // Convert Uint8Array to base64
      let base64Image;
      if (imageBuffer instanceof Uint8Array) {
        const binaryString = Array.from(imageBuffer, (byte) =>
          String.fromCharCode(byte)
        ).join("");
        base64Image = btoa(binaryString);
      } else {
        base64Image = imageBuffer.toString("base64");
      }

      const systemPrompt = `You are an expert in pharmaceutical packaging analysis. 
Your task is to analyze the provided image and count the number of capsules in a strip packaging.

INSTRUCTIONS:
1. Look for capsules wrapped in foil within the strip packaging. Each capsule should be clearly visible, intact, and wrapped in foil.
2. Count only those capsules that are completely sealed and undamaged. Avoid counting capsules that appear torn, partially visible, or damaged in any way.
3. If any areas of the foil strip show signs of tears, gaps, or broken seals, this indicates that those capsules may have been removed. Exclude any damaged or missing capsules from the count.
4. In cases where the strip packaging appears convex (bulging outward), this typically suggests that the capsules are still inside. Count these capsules as intact.
5. Ensure that capsules are fully visible and identifiable. If the image is unclear or the capsules are obscured, count only those that are fully visible and properly sealed.
6. After analyzing the image, provide a confidence score (out of 100) based on the clarity of the image, the visibility of the capsules, and the extent to which the capsules are intact and properly sealed.

Respond with JSON in this format:
{
  "capsuleCount": number,
  "confidence": number (0.0-1.0),
  "description": "Brief description of what you found"
}`;

      const requestBody = {
        contents: [
          {
            parts: [
              {
                text: systemPrompt,
              },
              {
                inlineData: {
                  data: base64Image,
                  mimeType: "image/jpeg",
                },
              },
              {
                text: "Based on the image provided, count the number of capsules in the strip packaging. Each capsule is wrapped in foil. Analyze the foil packaging if there are tears in the strip packaging in several areas, indicating that the capsules have been taken by the mother. Some strip packaging that still contains capsules also tends to be convex, indicating that the capsules are still inside. Please determine the number of capsules still in the strip packaging based on the image.",
              },
            ],
          },
        ],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: {
            type: "object",
            properties: {
              capsuleCount: {
                type: "number",
                minimum: 0,
              },
              confidence: {
                type: "number",
                minimum: 0,
                maximum: 1,
              },
              description: { type: "string" },
            },
            required: ["capsuleCount", "confidence", "description"],
          },
        },
      };

      const response = await fetch(
        `${this.baseUrl}/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        logger.apiError("analyzeTTDCapsules", {
          message: errorText,
          status: response.status,
        });
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const data = await response.json();
      const rawJson = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (rawJson) {
        const result = JSON.parse(rawJson);
        result.confidence = Math.max(0, Math.min(1, result.confidence));
        return result;
      } else {
        throw new Error("Empty response from Gemini model");
      }
    } catch (error) {
      logger.apiError("analyzeTTDCapsules", error);
      throw new Error(`Failed to analyze TTD capsules: ${error.message}`);
    }
  }
}

// Create singleton instance
const geminiService = new GeminiService();

export default geminiService;
